import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mic2, Brain, Sparkles, ArrowRight, Flame, Clock, Layers, TrendingUp, Plus, GraduationCap, MessageCircle, ClipboardCheck } from "lucide-react";

/** Session modes offered on the dashboard. `ready` ones start a session; others show "Soon". */
const PRACTICE_MODES = [
  { id: "a1_sprechen", label: "A1 Speaking Exam", desc: "Goethe A1 — Teil 1, 2 & 3", Icon: GraduationCap, ready: true },
  { id: "free_conversation", label: "Free Conversation", desc: "Open, natural dialogue", Icon: MessageCircle, ready: true },
  { id: "mock_test", label: "Mock Test", desc: "Timed, scored A1 exam", Icon: ClipboardCheck, ready: true, route: "/dashboard/mock-test" },
  { id: "pronunciation", label: "Pronunciation", desc: "Sound & accent focus", Icon: Mic2, ready: false },
];
import { useDashboardData } from "../../hooks/useDashboardData";
import { SectionHeader, StatChip, MemoryChip, ScenarioCard, Sparkline, AccentDot } from "../components/Shared";
import { getBackendUrl } from "@/lib/utils";
import { supabase } from "../../lib/supabase";

const BACKEND = getBackendUrl();

// ─── Loading Skeleton ─────────────────────────────────────────────────────────

const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-panel/10 ${className}`} />
);

// ─── Greeting fetcher ─────────────────────────────────────────────────────────

async function fetchAIGreeting(profile, memory) {
  try {
    
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const res = await fetch(`${BACKEND}/api/greeting`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({ profile, memory }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.greeting || null;
  } catch {
    return null;
  }
}

// ─── Today's Brief generator from real memory ────────────────────────────────

function buildTodaysBrief(memory) {
  const brief = [];

  if (memory.next_focus && memory.next_focus !== "Your first conversation with AURA") {
    brief.push({
      id: "focus",
      icon: "Brain",
      title: memory.next_focus,
      reason: "Picked up from your last session.",
      time: "~10 min",
    });
  }

  // Top failed vocab drill
  const topFailed = (memory.failed_vocabulary || []).slice(0, 1)[0];
  if (topFailed) {
    brief.push({
      id: "vocab",
      icon: "Mic2",
      title: `Practice "${topFailed.term}"`,
      reason: "You stumbled on this recently. Let's cement it.",
      time: "~5 min",
    });
  }

  // Working on items
  const topWorking = (memory.working_on || []).slice(0, 1)[0];
  if (topWorking && topWorking !== "Getting started") {
    brief.push({
      id: "working",
      icon: "Sparkles",
      title: topWorking,
      reason: "Flagged as an area to reinforce.",
      time: "~8 min",
    });
  }

  // Fallback for brand-new users
  if (brief.length === 0) {
    brief.push({
      id: "first",
      icon: "Sparkles",
      title: "Start your first session",
      reason: "AURA will personalise everything after she hears you speak.",
      time: "~15 min",
    });
  }

  return brief.slice(0, 3);
}

// ─── Memory node builder from real memory_json ────────────────────────────────

function buildMemoryNodes(memory) {
  const nodes = [];

  (memory.mastered_vocabulary || []).slice(-4).forEach((v, i) => {
    nodes.push({
      id: `mastered_${i}`,
      label: v.term,
      category: "vocabulary",
      accent: "cyan",
      detail: `Mastered on ${v.mastered_on ? new Date(v.mastered_on).toLocaleDateString() : "recently"}.`,
    });
  });

  (memory.personal_context || []).slice(-2).forEach((p, i) => {
    nodes.push({
      id: `ctx_${i}`,
      label: p.fact,
      category: "goal",
      accent: "violet",
      detail: `Remembered since ${p.first_mentioned ? new Date(p.first_mentioned).toLocaleDateString() : "your first session"}.`,
    });
  });

  (memory.strengths || []).slice(-2).forEach((s, i) => {
    nodes.push({
      id: `str_${i}`,
      label: s,
      category: "grammar",
      accent: "blue",
      detail: "A consistent strength AURA has noticed.",
    });
  });

  return nodes.slice(0, 4);
}

const ICONS = { Mic2, Brain, Sparkles };

// ─── Today page ───────────────────────────────────────────────────────────────

const Today = () => {
  const { user, memory, sessionStats, isLoading, isNewUser } = useDashboardData();
  const [aiGreeting, setAiGreeting] = useState(null);
  const [greetingLoaded, setGreetingLoaded] = useState(false);
  const didFetch = useRef(false);

  const greet = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  // Fetch AI greeting once user + memory are loaded
  useEffect(() => {
    if (isLoading || didFetch.current) return;
    didFetch.current = true;
    fetchAIGreeting(user, memory).then((g) => {
      setAiGreeting(g);
      setGreetingLoaded(true);
    });
  }, [isLoading, user, memory]);

  const displayName = user?.display_name || user?.target_language ? `${user.target_language} learner` : "there";
  const fallbackGreeting = `${greet}, ${displayName}.`;
  const headingText = greetingLoaded && aiGreeting ? aiGreeting : fallbackGreeting;

  const todaysBrief = buildTodaysBrief(memory);
  const memoryNodes = buildMemoryNodes(memory);

  const cefrValue = memory.cefr_level || "A1";
  const streak = sessionStats.streak;
  const minutes = sessionStats.minutesThisWeek;
  const memCount = sessionStats.memoryCount;

  const navigate = useNavigate();
  const startInMode = (modeId) => {
    localStorage.setItem("aura_mode", modeId);
    navigate("/dashboard/session");
  };

  // Continue card: show last session topic or fallback
  const lastSession = sessionStats.logs[0];
  const continueTitle = lastSession?.topic_summary || memory.next_focus || "Start your first conversation";
  const continueMode = lastSession?.mode?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Free Conversation";

  return (
    <div className="max-w-7xl mx-auto space-y-8" data-testid="page-today">
      {/* Greeting */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
          {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
        </div>
        {isLoading ? (
          <Skeleton className="mt-3 h-12 w-2/3" />
        ) : (
          <h1 className="mt-2 font-display text-[34px] sm:text-[44px] font-light tracking-tight text-ink leading-[1.05]">
            {headingText} <span className="serif-em">{isNewUser ? "Let's begin." : "She remembers."}</span>
          </h1>
        )}
      </motion.div>

      {/* Continue / start session hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
        className="relative rounded-3xl glass edge-glow p-6 sm:p-8 overflow-hidden"
        data-testid="today-continue-card"
      >
        <div aria-hidden className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full bg-aura-cyan/15 blur-[140px]" />
        <div aria-hidden className="absolute -bottom-24 -left-24 w-[260px] h-[260px] rounded-full bg-aura-violet/15 blur-[140px]" />
        <div className="relative grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8 min-w-0">
            <div className="section-label">{isNewUser ? "Begin your journey" : "Continue your story"}</div>
            {isLoading ? (
              <Skeleton className="mt-3 h-9 w-3/4" />
            ) : (
              <h2 className="mt-3 font-display text-[26px] sm:text-[34px] font-light tracking-tight text-ink leading-tight">
                {isNewUser ? (
                  <>Introduce yourself to AURA — she'll <em>never forget</em> what she learns.</>
                ) : (
                  <em>{continueTitle}</em>
                )}
              </h2>
            )}
            {!isLoading && (
              <p className="mt-3 text-whisper text-[14.5px] max-w-2xl">
                {isNewUser
                  ? "Your first session sets the foundation. AURA will adapt everything to your voice."
                  : lastSession
                  ? `Last session was ${continueMode}. AURA is ready to pick up where you left off.`
                  : `AURA is ready whenever you are.`}
              </p>
            )}
            {!isLoading && !isNewUser && (
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="pill">{cefrValue} · session</span>
                {lastSession && (
                  <span className="pill border-panel/10 bg-panel/[0.02] text-whisper">
                    {lastSession.duration_minutes}m last time
                  </span>
                )}
                {memory.failed_vocabulary?.length > 0 && (
                  <span className="pill border-panel/10 bg-panel/[0.02] text-whisper">
                    {memory.failed_vocabulary.length} vocab to revisit
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/dashboard/session"
              className="btn-primary w-full lg:w-auto justify-center text-[12px]"
              data-testid="today-continue-btn"
            >
              {isNewUser ? "Start first session" : "Resume session"} <Mic2 size={14} />
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Choose your practice — session mode picker */}
      <div data-testid="today-modes">
        <SectionHeader eyebrow="Choose your practice" title="What do you want to do today?" />
        <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {PRACTICE_MODES.map(({ id, label, desc, Icon, ready, route }) => (
            <button
              key={id}
              type="button"
              disabled={!ready}
              onClick={() => ready && (route ? navigate(route) : startInMode(id))}
              data-testid={`today-mode-${id}`}
              className={`relative text-left glass rounded-2xl p-4 hairline transition-colors ${
                ready ? "hover:border-aura-cyan/50 cursor-pointer" : "opacity-55 cursor-not-allowed"
              }`}
            >
              {!ready && (
                <span className="absolute top-3 right-3 pill text-[9px] border-panel/10 bg-panel/[0.03] text-whisper">Soon</span>
              )}
              <div className="w-10 h-10 rounded-xl border border-aura-cyan/25 bg-aura-cyan/5 grid place-items-center text-aura-cyan">
                <Icon size={17} strokeWidth={1.5} />
              </div>
              <div className="mt-3 font-display text-[15px] font-medium text-ink">{label}</div>
              <div className="text-[12px] text-whisper">{desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="today-stats">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)
        ) : (
          <>
            <StatChip label="Streak" value={streak > 0 ? `${streak} d` : "—"} delta={streak > 0 ? "+1 today" : "Start today"} icon={Flame} />
            <StatChip label="This week" value={minutes > 0 ? `${minutes}m` : "—"} delta={minutes > 0 ? `${minutes}m practiced` : "No sessions yet"} icon={Clock} />
            <StatChip label="Memories" value={memCount > 0 ? String(memCount) : "—"} delta={memCount > 0 ? `${memCount} nodes` : "Builds with sessions"} icon={Layers} />
            <StatChip label="CEFR" value={cefrValue} delta={isNewUser ? "Just starting" : "Current estimate"} icon={TrendingUp} />
          </>
        )}
      </div>

      {/* Two-column: Today's brief + memory feed */}
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7 space-y-4">
          <SectionHeader eyebrow="Today's brief" title={isNewUser ? "Where to begin." : "Three things AURA wants to do."} />
          <div className="space-y-3">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)
            ) : (
              todaysBrief.map((b, i) => {
                const Icon = ICONS[b.icon] || Sparkles;
                return (
                  <motion.div
                    key={b.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.05 * i }}
                    className="glass rounded-2xl p-5 hairline flex items-center justify-between gap-4"
                    data-testid={`today-brief-${i}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-11 h-11 rounded-xl border border-aura-cyan/25 bg-aura-cyan/5 grid place-items-center text-aura-cyan shrink-0">
                        <Icon size={17} strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display text-[16px] sm:text-[18px] font-medium text-ink truncate">{b.title}</div>
                        <div className="text-[13px] text-whisper">{b.reason}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="hidden sm:inline font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">{b.time}</span>
                      <Link to="/dashboard/session" className="btn-ghost text-[10px] py-2 px-3" data-testid={`today-brief-start-${i}`}>start</Link>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>

        <div className="lg:col-span-5 space-y-4">
          <SectionHeader
            eyebrow="Memory feed"
            title={isNewUser ? "She's listening." : "Latest she's remembered."}
            action={!isNewUser && (
              <Link to="/dashboard/memory" className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-aura-cyan hover:text-ink flex items-center gap-1.5" data-testid="today-memory-all">
                Open graph <ArrowRight size={11} />
              </Link>
            )}
          />
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
            </div>
          ) : memoryNodes.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="today-memory-list">
              {memoryNodes.map((n) => <MemoryChip key={n.id} node={n} />)}
            </div>
          ) : (
            <div className="rounded-2xl hairline p-6 text-center space-y-2">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">no memories yet</div>
              <div className="font-serif italic text-[16px] text-ink/80">Complete a session and AURA will remember everything.</div>
            </div>
          )}
        </div>
      </div>

      {/* Goal progress */}
      <div className="grid lg:grid-cols-12 gap-5">
        <motion.div
          initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="lg:col-span-7 glass rounded-3xl p-6 hairline"
          data-testid="today-goal-card"
        >
          <div className="flex items-center justify-between">
            <div className="section-label">Your goal</div>
            <Link to="/dashboard/goals" className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper hover:text-ink">manage</Link>
          </div>
          {isLoading ? (
            <Skeleton className="mt-3 h-8 w-3/4" />
          ) : (
            <>
              <h3 className="mt-3 font-display text-[24px] font-light tracking-tight text-ink">
                {user?.learning_goal || "Learn to speak confidently"} ·{" "}
                <span className="text-whisper">{memory.next_focus || "Getting started"}</span>
              </h3>
              <div className="mt-5 h-2 rounded-full bg-panel/5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, (sessionStats.logs.length / 20) * 100)}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-aura-cyan via-aura-blue to-aura-violet"
                />
              </div>
              <div className="mt-3 flex items-center justify-between font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
                <span>{sessionStats.logs.length} session{sessionStats.logs.length !== 1 ? "s" : ""} completed</span>
                <span className="text-aura-cyan">{cefrValue} level</span>
              </div>
            </>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.05 }}
          className="lg:col-span-5 glass rounded-3xl p-6 hairline"
          data-testid="today-signal-card"
        >
          <div className="section-label">Cognitive signals</div>
          {isLoading ? (
            <div className="mt-4 grid grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-2 gap-3">
              {[
                { label: "Strengths", value: memory.strengths.length, delta: memory.strengths.length > 0 ? `+${memory.strengths.length}` : "none", tone: memory.strengths.length > 0 ? "up" : "neutral" },
                { label: "Vocab Gaps", value: memory.failed_vocabulary.length, delta: memory.failed_vocabulary.length > 0 ? `${memory.failed_vocabulary.length} flagged` : "clean", tone: memory.failed_vocabulary.length > 0 ? "down" : "neutral" },
                { label: "Mastered", value: memory.mastered_vocabulary.length, delta: memory.mastered_vocabulary.length > 0 ? `+${memory.mastered_vocabulary.length}` : "none", tone: memory.mastered_vocabulary.length > 0 ? "up" : "neutral" },
                { label: "Context", value: memory.personal_context.length, delta: `${memory.personal_context.length} facts`, tone: "neutral" },
              ].map((s) => (
                <div key={s.label} className="rounded-xl border border-panel/8 bg-elev/40 p-3">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-whisper-muted">{s.label}</div>
                    <AccentDot accent={s.tone === "down" ? "violet" : "cyan"} />
                  </div>
                  <div className="mt-2 flex items-end gap-2">
                    <div className="font-display text-[20px] font-light leading-none tabular-nums text-ink">{s.value}</div>
                    <div className={`font-mono text-[10px] tracking-[0.16em] uppercase ${s.tone === "up" ? "text-aura-cyan" : s.tone === "down" ? "text-red-300" : "text-whisper-muted"}`}>{s.delta}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Add memory CTA */}
      <div className="rounded-3xl border border-dashed border-panel/15 p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="section-label">Add it manually</div>
          <h3 className="mt-2 font-display text-[20px] font-light tracking-tight text-ink">Notice something between sessions?</h3>
          <p className="mt-1 text-whisper text-[13.5px]">Drop a thought · she'll save it.</p>
        </div>
        <Link to="/dashboard/memory" className="btn-ghost" data-testid="today-add-memory">
          <Plus size={14} /> add memory
        </Link>
      </div>
    </div>
  );
};

export default Today;
